<template>
    <Dialog
        :visible="visible"
        @update:visible="(value) => !value && requestClose()"
        modal
        header="Edit Profile"
        :draggable="false"
        :style="{ width: '40rem' }"
        :breakpoints="{ '640px': '95vw' }">
        <div v-if="loading" class="flex justify-content-center p-5">
            <ProgressSpinner style="width: 3rem; height: 3rem" />
        </div>

        <div v-else-if="loadError">
            <Message severity="error" :closable="false">{{
                loadError
            }}</Message>
            <Button label="Retry" icon="far fa-rotate-right" @click="load()" />
        </div>

        <form v-else @submit.prevent>
            <div
                class="mb-3 p-3 border-1 surface-border border-round-md surface-section flex align-items-center gap-3">
                <!-- The avatar upload only works for the signed-in user. -->
                <template v-if="editingSelf">
                    <button
                        type="button"
                        class="avatar-button flex-shrink-0"
                        aria-label="Change profile picture"
                        v-tooltip.bottom="'Change profile picture'"
                        :disabled="uploadingAvatar"
                        @click="avatarInput?.click()">
                        <UserAvatar :user="profileUser" size="xlarge" />
                        <span v-if="uploadingAvatar" class="avatar-uploading">
                            <ProgressSpinner
                                stroke-width="6"
                                style="width: 1.75rem; height: 1.75rem" />
                        </span>
                        <span v-else class="avatar-edit-badge">
                            <i class="fass fa-pencil"></i>
                        </span>
                    </button>
                    <input
                        ref="avatarInput"
                        type="file"
                        accept="image/*"
                        class="hidden"
                        @change="onAvatarSelected" />
                </template>
                <UserAvatar
                    v-else
                    :user="profileUser"
                    size="xlarge"
                    class="flex-shrink-0" />
                <div class="flex-1 min-w-0">
                    <div
                        class="font-medium text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {{ fullName || profileUser?.email }}
                    </div>
                    <div
                        class="text-sm text-color-secondary white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {{ profileUser?.email }}
                    </div>
                </div>
            </div>

            <div
                class="mb-3 p-3 border-1 surface-border border-round-md surface-section">
                <h4 class="text-sm font-medium text-color-secondary mt-0 mb-3">
                    Personal Information
                </h4>

                <div class="formgrid grid">
                    <div class="field col-12 md:col-6">
                        <FieldLabel
                            for="profileFirstName"
                            class="block text-900 font-medium mb-2"
                            required
                            >First Name</FieldLabel
                        >
                        <InputText
                            id="profileFirstName"
                            v-model="form.firstName"
                            placeholder="Enter first name"
                            autocomplete="given-name"
                            class="w-full"
                            :invalid="!!errors.firstName" />
                        <small
                            v-if="errors.firstName"
                            class="block text-red-500"
                            >{{ errors.firstName }}</small
                        >
                    </div>

                    <div class="field col-12 md:col-6">
                        <FieldLabel
                            for="profileLastName"
                            class="block text-900 font-medium mb-2"
                            required
                            >Last Name</FieldLabel
                        >
                        <InputText
                            id="profileLastName"
                            v-model="form.lastName"
                            placeholder="Enter last name"
                            autocomplete="family-name"
                            class="w-full"
                            :invalid="!!errors.lastName" />
                        <small
                            v-if="errors.lastName"
                            class="block text-red-500"
                            >{{ errors.lastName }}</small
                        >
                    </div>
                </div>

                <div class="field">
                    <FieldLabel
                        for="profileDisplayName"
                        class="block text-900 font-medium mb-2"
                        required
                        >Display Name</FieldLabel
                    >
                    <InputText
                        id="profileDisplayName"
                        v-model="form.displayName"
                        placeholder="Enter display name"
                        autocomplete="nickname"
                        class="w-full"
                        :invalid="!!errors.displayName" />
                    <small
                        v-if="errors.displayName"
                        class="block text-red-500"
                        >{{ errors.displayName }}</small
                    >
                </div>

                <div class="field">
                    <FieldLabel
                        for="profileBirthday"
                        class="block text-900 font-medium mb-2"
                        >Date of Birth</FieldLabel
                    >
                    <Calendar
                        input-id="profileBirthday"
                        v-model="form.birthday"
                        date-format="M d, yy"
                        placeholder="Select a date"
                        :max-date="today"
                        show-button-bar
                        show-icon
                        icon-display="input"
                        class="w-full" />
                </div>

                <div class="field">
                    <FieldLabel
                        for="profileMobileNumber"
                        class="block text-900 font-medium mb-2"
                        required
                        >Cell Phone Number</FieldLabel
                    >
                    <InputText
                        id="profileMobileNumber"
                        v-model="form.mobileNumber"
                        type="tel"
                        placeholder="Enter cell phone number"
                        autocomplete="tel"
                        class="w-full"
                        :invalid="!!errors.mobileNumber" />
                    <small
                        v-if="errors.mobileNumber"
                        class="block text-red-500"
                        >{{ errors.mobileNumber }}</small
                    >
                    <div
                        v-else-if="showReceiveSms"
                        class="flex align-items-center gap-2 mt-2">
                        <Checkbox
                            input-id="profileReceiveSms"
                            v-model="form.receiveSms"
                            binary />
                        <label for="profileReceiveSms" class="text-sm"
                            >Receive SMS notifications</label
                        >
                    </div>
                    <small
                        v-else-if="showPhoneNotValidated && editingSelf"
                        class="block text-500 mt-1"
                        >This phone number is not validated yet. Please check
                        your text messages for a validation link after saving
                        the changes.</small
                    >
                    <small
                        v-else-if="showPhoneNotValidated"
                        class="block text-500 mt-1"
                        >This phone number is not validated yet. The user gets a
                        text message with a validation link after saving.</small
                    >
                </div>

                <div class="field mb-0">
                    <label
                        for="profileEmail"
                        class="block text-900 font-medium mb-2"
                        >Email Address</label
                    >
                    <InputText
                        id="profileEmail"
                        :model-value="profileUser?.email"
                        class="w-full"
                        disabled />
                </div>
            </div>

            <div
                class="mb-3 p-3 border-1 surface-border border-round-md surface-section">
                <h4 class="text-sm font-medium text-color-secondary mt-0 mb-3">
                    Additional Details
                </h4>

                <div class="formgrid grid">
                    <div class="field col-12 md:col-4">
                        <label
                            for="profileBloodType"
                            class="block text-900 font-medium mb-2"
                            >Blood Type</label
                        >
                        <Dropdown
                            input-id="profileBloodType"
                            v-model="form.bloodType"
                            :options="BLOOD_TYPE_OPTIONS"
                            placeholder="Select"
                            show-clear
                            class="w-full" />
                    </div>

                    <div class="field col-12 md:col-4">
                        <label
                            for="profileMobility"
                            class="block text-900 font-medium mb-2"
                            >Mobility Level</label
                        >
                        <Dropdown
                            input-id="profileMobility"
                            v-model="form.mobility"
                            :options="MOBILITY_OPTIONS"
                            placeholder="Select"
                            show-clear
                            class="w-full" />
                    </div>

                    <div class="field col-12 md:col-4">
                        <label
                            for="profileFirstAid"
                            class="block text-900 font-medium mb-2"
                            >First Aid Certified</label
                        >
                        <Dropdown
                            input-id="profileFirstAid"
                            v-model="form.isFirstAidCertified"
                            :options="YES_NO_OPTIONS"
                            option-label="label"
                            option-value="value"
                            class="w-full" />
                    </div>
                </div>

                <div class="field mb-0">
                    <label
                        for="profileNotes"
                        class="block text-900 font-medium mb-2"
                        >Notes</label
                    >
                    <Textarea
                        id="profileNotes"
                        v-model="form.notes"
                        rows="3"
                        auto-resize
                        class="w-full" />
                </div>
            </div>

            <div
                class="p-3 border-1 surface-border border-round-md surface-section">
                <h4 class="text-sm font-medium text-color-secondary mt-0 mb-3">
                    Emergency Contacts
                </h4>

                <div
                    v-for="(contact, index) in form.emergencyContacts"
                    :key="contact.key"
                    class="mb-3 p-3 border-1 surface-border border-round-md">
                    <div
                        class="flex align-items-center justify-content-between mb-2">
                        <span class="font-medium text-900"
                            >Contact {{ index + 1 }}</span
                        >
                        <Button
                            label="Delete"
                            icon="far fa-trash"
                            severity="danger"
                            size="small"
                            text
                            @click="confirmDeleteContact(index)" />
                    </div>

                    <div class="formgrid grid">
                        <div class="field col-12 md:col-6">
                            <FieldLabel
                                :for="`contact-${contact.key}-name`"
                                class="block text-900 font-medium mb-2"
                                required
                                >Name</FieldLabel
                            >
                            <InputText
                                :id="`contact-${contact.key}-name`"
                                v-model="contact.displayName"
                                placeholder="Enter contact name"
                                class="w-full"
                                :invalid="
                                    !!errors[`contact-${contact.key}-name`]
                                " />
                            <small
                                v-if="errors[`contact-${contact.key}-name`]"
                                class="block text-red-500"
                                >{{
                                    errors[`contact-${contact.key}-name`]
                                }}</small
                            >
                        </div>

                        <div class="field col-12 md:col-6">
                            <label
                                :for="`contact-${contact.key}-relationship`"
                                class="block text-900 font-medium mb-2"
                                >Relationship</label
                            >
                            <InputText
                                :id="`contact-${contact.key}-relationship`"
                                v-model="contact.relationship"
                                placeholder="Enter relationship"
                                class="w-full" />
                        </div>

                        <div class="field col-12 md:col-6 mb-0">
                            <label
                                :for="`contact-${contact.key}-email`"
                                class="block text-900 font-medium mb-2"
                                >Email</label
                            >
                            <InputText
                                :id="`contact-${contact.key}-email`"
                                v-model="contact.email"
                                type="email"
                                placeholder="Enter email address"
                                class="w-full"
                                :invalid="
                                    !!errors[`contact-${contact.key}-email`]
                                " />
                            <small
                                v-if="errors[`contact-${contact.key}-email`]"
                                class="block text-red-500"
                                >{{
                                    errors[`contact-${contact.key}-email`]
                                }}</small
                            >
                        </div>

                        <div class="field col-12 md:col-6 mb-0">
                            <label
                                :for="`contact-${contact.key}-phone`"
                                class="block text-900 font-medium mb-2"
                                >Phone</label
                            >
                            <InputText
                                :id="`contact-${contact.key}-phone`"
                                v-model="contact.phone"
                                type="tel"
                                placeholder="Enter phone number"
                                class="w-full" />
                        </div>
                    </div>
                </div>

                <Button
                    :label="
                        form.emergencyContacts.length
                            ? 'Add Another Emergency Contact'
                            : 'Add Emergency Contact'
                    "
                    icon="far fa-circle-plus"
                    size="small"
                    text
                    @click="addContact()" />
            </div>
        </form>

        <template #footer>
            <!-- The footer is a flex row; the column keeps a long error off the buttons' line. -->
            <div class="flex flex-column w-full">
                <Message
                    v-if="saveError"
                    severity="error"
                    :closable="false"
                    class="text-left mt-3 mb-0"
                    >Unable to save your changes. {{ saveError }}</Message
                >
                <div class="flex justify-content-end gap-2 mt-3">
                    <Button
                        label="Cancel"
                        severity="secondary"
                        text
                        class="m-0"
                        :disabled="saving"
                        @click="requestClose()" />
                    <Button
                        label="Save"
                        class="m-0"
                        :loading="saving"
                        :disabled="!canSave"
                        @click="save()" />
                </div>
            </div>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import * as yup from "yup";
import { ToastSeverity } from "primevue/api";
import { useConfirm } from "primevue/useconfirm";
import { useMe } from "@/composables/queries/useMe";
import { useToastService } from "@/composables/useToastService";
import { useAuthenticationStore } from "@/stores/authentication";
import { useConsumerApi } from "@/composables/useConsumerApi";
import { getUserFullName } from "@/utils/userNames";
import UserAvatar from "@/components/shared/UserAvatar.vue";
import { getApiErrorMessage, isValidPhoneNumber } from "@/utils/validation";
import type {
    CurrentUserResponse,
    EmergencyContact,
    ProfileUpdateRequest,
    UserPhoneInfo,
} from "@/types/api";

const visible = defineModel<boolean>("visible", { required: true });

const props = defineProps<{
    /** Edit this user instead of the signed-in one (admin edit). */
    userId?: number | null;
}>();

const emit = defineEmits<{
    saved: [user: CurrentUserResponse];
}>();

// Hardcoded in iOS too; the API accepts any string.
const BLOOD_TYPE_OPTIONS = [
    "APos",
    "ANeg",
    "BPos",
    "BNeg",
    "ABPos",
    "ABNeg",
    "OPos",
    "ONeg",
];
const MOBILITY_OPTIONS = ["Low", "Medium", "High"];
const YES_NO_OPTIONS = [
    { label: "Yes", value: true },
    { label: "No", value: false },
];
const AVATAR_MAX_SIZE = 400;

const confirm = useConfirm();
const { showToast } = useToastService();
const authStore = useAuthenticationStore();
const consumerApi = useConsumerApi();
const { user } = useMe();

const otherUser = ref<CurrentUserResponse | null>(null);
const editingSelf = computed(
    () => !props.userId || props.userId === user.value?.id,
);
const profileUser = computed(() =>
    editingSelf.value ? user.value : otherUser.value,
);

// `key` gives v-for a stable identity; it is stripped before saving.
type ContactForm = EmergencyContact & { key: number };

const form = reactive({
    firstName: "",
    lastName: "",
    displayName: "",
    birthday: null as Date | null,
    mobileNumber: "",
    receiveSms: false,
    bloodType: null as string | null,
    mobility: null as string | null,
    isFirstAidCertified: false,
    notes: "",
    emergencyContacts: [] as ContactForm[],
});

const loading = ref(false);
const loadError = ref("");
const saving = ref(false);
const saveError = ref("");
const uploadingAvatar = ref(false);
const avatarInput = ref<HTMLInputElement | null>(null);
const baseline = ref("");
const originalPhone = ref<UserPhoneInfo | undefined>();
let nextContactKey = 0;

const today = new Date();

const fullName = computed(() => getUserFullName(profileUser.value?.profile));

// Birthdays are stored as UTC midnight; reading only the date part keeps
// western timezones from showing the previous day.
const parseBirthday = (value?: string | null): Date | null => {
    const match = value && /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    return match ? new Date(+match[1], +match[2] - 1, +match[3]) : null;
};

const formatBirthday = (date: Date): string => {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00.000Z`;
};

const initForm = (currentUser: CurrentUserResponse) => {
    const profile = currentUser.profile;
    originalPhone.value = currentUser.phoneInfo?.[0];

    form.firstName = profile?.firstName ?? "";
    form.lastName = profile?.lastName ?? "";
    form.displayName = currentUser.displayName ?? "";
    form.birthday = parseBirthday(profile?.birthday);
    form.mobileNumber = originalPhone.value?.mobileNumber ?? "";
    form.receiveSms = originalPhone.value?.receiveSms ?? false;
    form.bloodType = profile?.bloodType ?? null;
    form.mobility = profile?.mobility ?? null;
    form.isFirstAidCertified = profile?.isFirstAidCertified ?? false;
    form.notes = profile?.notes ?? "";
    form.emergencyContacts = (profile?.emergencyContacts ?? []).map(
        (contact) => ({
            id: contact.id ?? null,
            displayName: contact.displayName ?? "",
            email: contact.email ?? "",
            phone: contact.phone ?? "",
            relationship: contact.relationship ?? "",
            profileId: contact.profileId ?? null,
            key: nextContactKey++,
        }),
    );

    baseline.value = JSON.stringify(form);
};

const load = async () => {
    loading.value = true;
    loadError.value = "";
    saveError.value = "";
    try {
        if (editingSelf.value) {
            initForm(await authStore.fetchCurrentUser());
        } else {
            const { data } = await consumerApi.get<CurrentUserResponse>(
                `/users/${props.userId}`,
            );
            otherUser.value = data;
            initForm(data);
        }
    } catch (error) {
        console.error("Failed to load profile:", error);
        loadError.value = getApiErrorMessage(
            error,
            "We couldn't load this profile. Please try again.",
        );
    } finally {
        loading.value = false;
    }
};

watch(visible, (isVisible) => {
    if (isVisible) load();
});

const phoneNumberChanged = computed(
    () =>
        form.mobileNumber.trim() !==
        (originalPhone.value?.mobileNumber ?? "").trim(),
);
// SMS opt-in is only offered for an already verified number.
const showReceiveSms = computed(
    () =>
        !!form.mobileNumber.trim() &&
        originalPhone.value?.active === true &&
        !phoneNumberChanged.value,
);
const showPhoneNotValidated = computed(
    () =>
        !!form.mobileNumber.trim() &&
        (originalPhone.value?.active === false || phoneNumberChanged.value),
);

const emailSchema = yup.string().email();

const errors = computed(() => {
    const result: Record<string, string> = {};
    if (!form.firstName.trim()) result.firstName = "First name is required.";
    if (!form.lastName.trim()) result.lastName = "Last name is required.";
    if (!form.displayName.trim())
        result.displayName = "Display name is required.";

    const mobileNumber = form.mobileNumber.trim();
    if (!mobileNumber) result.mobileNumber = "Cell phone number is required.";
    else if (!isValidPhoneNumber(mobileNumber))
        result.mobileNumber = "Please enter a valid mobile number.";

    for (const contact of form.emergencyContacts) {
        if (!contact.displayName.trim())
            result[`contact-${contact.key}-name`] = "Contact name is required.";
        const email = contact.email.trim();
        if (email && !emailSchema.isValidSync(email))
            result[`contact-${contact.key}-email`] =
                "Please enter a valid email address.";
    }
    return result;
});

const isDirty = computed(() => JSON.stringify(form) !== baseline.value);
const canSave = computed(
    () =>
        !loading.value &&
        !loadError.value &&
        !uploadingAvatar.value &&
        Object.keys(errors.value).length === 0,
);

const addContact = () => {
    form.emergencyContacts.push({
        id: null,
        displayName: "",
        email: "",
        phone: "",
        relationship: "",
        profileId: profileUser.value?.profile?.id || null,
        key: nextContactKey++,
    });
};

const confirmDeleteContact = (index: number) => {
    confirm.require({
        header: "Delete Emergency Contact?",
        message:
            "This contact will be removed from the emergency contacts list.",
        icon: "far fa-triangle-exclamation",
        acceptLabel: "Delete",
        rejectLabel: "Cancel",
        acceptClass: "p-button-danger",
        rejectClass: "p-button-secondary p-button-text",
        accept: () => form.emergencyContacts.splice(index, 1),
    });
};

const requestClose = () => {
    if (saving.value) return;
    if (!isDirty.value) {
        visible.value = false;
        return;
    }
    confirm.require({
        header: "Discard Changes?",
        message:
            "You have unsaved changes. Do you want to exit without saving?",
        icon: "far fa-triangle-exclamation",
        acceptLabel: "Discard",
        rejectLabel: "Keep Editing",
        acceptClass: "p-button-danger",
        rejectClass: "p-button-secondary p-button-text",
        accept: () => {
            visible.value = false;
        },
    });
};

const buildRequest = (
    currentUser: CurrentUserResponse,
): ProfileUpdateRequest => {
    const profile = currentUser.profile;
    // Resending an unverified number texts the validation link again, so an
    // admin edit only sends the phone when it changed.
    const sendPhone =
        editingSelf.value ||
        phoneNumberChanged.value ||
        form.receiveSms !== (originalPhone.value?.receiveSms ?? false);
    // An empty contact list is sent as null, matching the iOS app.
    return {
        userId: currentUser.id,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        displayName: form.displayName.trim(),
        avatar: profile?.avatar ?? null,
        // Not editable here; passed through so a save doesn't clear them.
        gender: profile?.gender ?? null,
        height: profile?.height ?? null,
        notes: form.notes.trim(),
        birthday: form.birthday ? formatBirthday(form.birthday) : null,
        bloodType: form.bloodType,
        isFirstAidCertified: form.isFirstAidCertified,
        mobility: form.mobility,
        emergencyContacts: form.emergencyContacts.length
            ? form.emergencyContacts.map(({ key, ...contact }) => ({
                  ...contact,
                  displayName: contact.displayName.trim(),
                  email: contact.email.trim(),
                  phone: contact.phone.trim(),
                  relationship: contact.relationship.trim(),
              }))
            : null,
        ...(sendPhone && {
            phoneNumbers: [
                {
                    mobileNumber: form.mobileNumber.trim(),
                    receiveSms: form.receiveSms,
                },
            ],
        }),
    };
};

const save = async () => {
    if (!canSave.value || saving.value || !profileUser.value) return;

    saving.value = true;
    saveError.value = "";
    try {
        const request = buildRequest(profileUser.value);
        const saved = editingSelf.value
            ? await authStore.updateProfile(request)
            : (
                  await consumerApi.post<CurrentUserResponse>(
                      `/users/${request.userId}/profile`,
                      request,
                  )
              ).data;
        emit("saved", saved);
        visible.value = false;
        showToast({
            severity: ToastSeverity.SUCCESS,
            summary: "Profile updated",
            life: 3000,
        });
    } catch (error) {
        console.error("Failed to save profile:", error);
        saveError.value = getApiErrorMessage(error, "Invalid server response");
    } finally {
        saving.value = false;
    }
};

// Downscales to a JPEG like the iOS app, keeping uploads small.
const resizeImage = async (file: File): Promise<Blob> => {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(
        1,
        AVATAR_MAX_SIZE / Math.max(bitmap.width, bitmap.height),
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas
        .getContext("2d")!
        .drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    return new Promise((resolve, reject) =>
        canvas.toBlob(
            (blob) =>
                blob ? resolve(blob) : reject(new Error("Encode failed")),
            "image/jpeg",
            0.9,
        ),
    );
};

// The CDN only serves the new avatar once server-side processing finishes.
const waitForImage = async (url: string, attempts = 16): Promise<boolean> => {
    for (let attempt = 0; attempt < attempts; attempt++) {
        const loaded = await new Promise<boolean>((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = `${url}${url.includes("?") ? "&" : "?"}attempt=${attempt}`;
        });
        if (loaded) return true;
        await new Promise((resolve) => setTimeout(resolve, 2000));
    }
    return false;
};

const showAvatarError = (detail: string) =>
    showToast({
        severity: ToastSeverity.ERROR,
        summary: "Unexpected Error",
        detail,
        life: 5000,
    });

const onAvatarSelected = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;

    uploadingAvatar.value = true;
    try {
        let avatar: string;
        try {
            const image = await resizeImage(file);
            ({
                profile: { avatar },
            } = await authStore.uploadAvatar(image, "avatar.jpg"));
        } catch (error) {
            console.error("Avatar upload failed:", error);
            showAvatarError("We were unable to upload this profile picture!");
            return;
        }

        const ready = await waitForImage(avatar);
        // Refresh even on timeout: the server already replaced the old file, and Save sends this URL back.
        await authStore.fetchCurrentUser();
        if (!ready) {
            showAvatarError(
                "We were unable to complete the profile picture upload!",
            );
            return;
        }

        showToast({
            severity: ToastSeverity.SUCCESS,
            summary: "Profile Picture Updated!",
            life: 3000,
        });
    } finally {
        uploadingAvatar.value = false;
    }
};
</script>

<style scoped>
.avatar-button {
    position: relative;
    display: flex;
    padding: 0;
    border: none;
    background: transparent;
    border-radius: 50%;
    cursor: pointer;
}

.avatar-button:disabled {
    cursor: progress;
}

.avatar-button:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
}

.avatar-edit-badge {
    position: absolute;
    top: -0.125rem;
    right: -0.125rem;
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    border-radius: 50%;
    background: var(--surface-card);
    color: var(--text-color);
    border: 1px solid var(--surface-border);
    transition:
        background-color 0.2s,
        color 0.2s;
}

/* Must stay opaque; the theme's --surface-hover is translucent and lets the photo show through. */
.avatar-button:hover .avatar-edit-badge {
    background: var(--primary-color);
    color: var(--primary-color-text);
    border-color: var(--primary-color);
}

.avatar-uploading {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
}
</style>
